import React, {useState, useEffect, Fragment} from "react";
import { Card, CircularProgress, Typography } from "@mui/material";
import { Icon } from "../../../../common/component/icon/icon";
import { BarCodeIcon } from "../../../../common/component/icon/icon-custom";
import { t } from "i18next";
import { getReadOnlyField } from "../../../../common/field/renderer";
import { Parcel } from "../../../model/parcel";
import { ParcelDetail } from "../../../model/parcel-detail";
import { PickAndPackStatus } from "../../../constants/constants";
import { context } from "../../../../context/context";
import { qzManager } from "../../../qz/qz-manager";
import { notificationModel } from "../../../../common/component/notifications/notification-model";
import { parcelsService } from "../../../services/parcels";
import { getLabelFormat } from "../../../../constants/constants";
import { menuOperationModel } from "../../preparation-menu/preparation-menu-model";
import { find, some } from "lodash";
import * as moment from "moment/moment";

export interface IPickAndPackHeaderProps {
    operationCode: string;
    batchId: number;
    reloadAreaList: () => Promise<void>;
}

import areaSound from "../../../../common/sounds/area.mp3";
import errorSound from "../../../../common/sounds/error.mp3";
import labelSound from "../../../../common/sounds/label.mp3";

const ERROR_SOUND: HTMLAudioElement = new Audio(errorSound);
const AREA_SOUND: HTMLAudioElement = new Audio(areaSound);
const LABEL_SOUND: HTMLAudioElement = new Audio(labelSound);

export const PickAndPackHeader = (props: IPickAndPackHeaderProps) => {
    const [eanScanned, setEanScanned] = useState<string | undefined>(undefined);
    const [area, setArea] = useState<number | undefined>(undefined);
    const [itemScanned, setItemScanned] = useState<Parcel | undefined>(undefined);
    const [detailItemScanned, setDetailItemScanned] = useState<ParcelDetail | undefined>(undefined);
    const [status, setStatus] = useState<PickAndPackStatus | undefined>(undefined);
    const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);

    useEffect(() => {
        window.addEventListener("keydown", callKeyDownEvent);
        window.addEventListener("keyup", callKeyUpEvent);

        return () => {
            window.removeEventListener("keydown", callKeyDownEvent);
            window.removeEventListener("keyup", callKeyUpEvent);
        };
    }, [props.operationCode, props.batchId]);

    let _eanStreamed: string = "";
    let _eanStreamedInputMoment: moment.Moment;

    const callKeyUpEvent = (e: any) => {
        e.stopPropagation();
        e.preventDefault();
        fireEanStreamed(e, onScan);
    };

    const callKeyDownEvent = (e: any) => {
        e.stopPropagation();
        e.preventDefault();
        listenNumericKeyBoard(e);
    };

    const listenNumericKeyBoard = (e: any) => {
        if (_eanStreamedInputMoment && moment().diff(_eanStreamedInputMoment, "ms") > 500) {
            _eanStreamed = "";
        }
        _eanStreamedInputMoment = moment();
        if (e.keyCode >= 33 && e.keyCode <= 126) {
            _eanStreamed = _eanStreamed + e.key;
        }
    };

    const fireEanStreamed = (e: any, scanAction: (n: string) => any) => {
        if (e.keyCode === 13) {
            scanAction(_eanStreamed);
            _eanStreamed = "";
        }
    };

    const onScan = async (value: string) => {
        await pickAndPack(value, props.operationCode, Number(props.batchId));

        await props.reloadAreaList();
    };

    const renderScanDetail = () => {
        if (status === "Labeled") {
            if (area) {
                return <div>
                    <Typography variant="h6">{t("pickAndPack.message.printAndGetArea", {
                        area,
                        deliveryOrderId: itemScanned !== undefined ? itemScanned.deliveryOrderId : "",
                    })}</Typography>
                </div>;
            } else {
                return <div>
                    <Typography variant="h6">{t("pickAndPack.message.printWithoutArea", {
                        parcelId: itemScanned !== undefined ? itemScanned.parcelId : "",
                        deliveryOrderId: itemScanned !== undefined ? itemScanned.deliveryOrderId : "",
                    })}</Typography>
                </div>;
            }
        } else if (status === "Processing") {
            return <div/>;
        }
        if (itemScanned != null) {
            return <div className="itemScanned">
                {renderOrderLine()}
                {renderArticleLine()}
            </div>;
        }
        if (status === "Error") {
            return <div className="errorMessage">
                <Typography variant="h6" data-testid="ppack-error-message">{errorMessage}</Typography>
            </div>;
        }
        return <div>
            <Typography variant="h6">{t("pickAndPack.message.noItemScanned")}</Typography>
        </div>;
    };

    const renderOrderLine = () => {
        if (itemScanned !== undefined) {
            return <div className="level1-field-container level1 content">
                {getReadOnlyField(itemScanned, "orderId")}
                {getReadOnlyField(itemScanned, "parcelId")}
                {getReadOnlyField(itemScanned, "deliveryOrderId")}
            </div>;
        }

        return <div className="level1-field-container level1 content"></div>;
    };

    const renderArticleLine = () => {
        if (detailItemScanned !== undefined) {
            return <div className="level2-field-container level2 content">
                {getReadOnlyField({eanScanned}, "eanScanned")}
                {getReadOnlyField(detailItemScanned, "labelReference")}
                {getReadOnlyField(detailItemScanned, "supplierReference")}
            </div>;
        }

        return <div className="level2-field-container level2 content">
            {getReadOnlyField({eanScanned}, "eanScanned")}
        </div>;
    };

    const renderIndicator = () => {
        if (status === "Processing") {
            return <CircularProgress className="circular-progress"/>;
        }
        if (status === "HoldInArea") {
            return <Typography className="iconIndicator">
                {area}
            </Typography>;
        }
        if (status === "Labeled") {
            return <Fragment>
                <Icon icon="print" className="iconIndicator" tooltipTitle="pickAndPack.tooltip.area" tooltipPlacement="top-start"/>
                {area &&
                    <Typography className="iconIndicator iconIndicator--blue">
                        {area}
                    </Typography>}
            </Fragment>;
        }
        if (status === "Error") {
            return <Icon icon="error" className="errorIndicator"/>;
        }
        return <div className="font35 iconIndicator">{t("pickAndPack.action.scan")}</div>;
    };

    const pickAndPack = async (eanScannedNew: string, operationCode: string, batchId: number) => {
        if (status !== "Processing") {
            if (!context.settings.isDownload) {
                const error = await qzManager.checkPrintConfig();
                if (error) {
                    notificationModel.addErrorMessage(error);
                    return;
                }
            }

            setEanScanned(eanScannedNew);
            setArea(undefined);
            setStatus("Processing");
            setItemScanned(undefined);
            setDetailItemScanned(undefined);
            setErrorMessage(undefined);

            try {
                const response = await parcelsService.pickAndPack(operationCode,
                    batchId,
                    eanScannedNew,
                    getLabelFormat(context.settings.printerType));
                // Load itemScanned
                const parcel = await parcelsService.loadParcelByParcelId(operationCode,
                    batchId,
                    response.parcelId);

                setArea(response.area);
                setStatus(response.status);
                setItemScanned(parcel);
                setDetailItemScanned(getDetailItemScanned(eanScannedNew, parcel));
                setErrorMessage(undefined);

                if (response.status === "Labeled") {
                    playLabelSound();
                    const file = response.labelResult.loadFile();
                    await qzManager.qzPrintOrDownloadFile(file, context.settings.printerType);
                } else {
                    playAreaSound();
                }
            } catch (e) {
                playErrorSound();
                setStatus("Error");
                setErrorMessage(e.message);
            }
        }

        await refreshOperationCounters(operationCode, batchId);
    };

    const getDetailItemScanned = (eanScannedNew: string, parcel: Parcel) => {
        if (parcel) {
            return find(parcel.details, (pad: ParcelDetail) => {
                return some(pad.ean13List, (ean: string) => {
                    return ean === eanScannedNew;
                }) === true;
            });
        }
        return undefined;
    };

    const refreshOperationCounters = async (operationCode: string, batchId: number) => {
        await menuOperationModel.refreshCounters(operationCode, batchId);
    };

    const playErrorSound = () => {
        ERROR_SOUND.play();
    };

    const playAreaSound = () => {
        AREA_SOUND.play();
    };

    const playLabelSound = () => {
        LABEL_SOUND.play();
    };

    return <Card className="pickcontainer">
        <div className="pickinstructions">
            <div className="areaindicator" data-testid="area-indicator">
                <div className="iconaction">
                    <Icon icon={<BarCodeIcon className="material-icons"/>}
                          className={status === "Processing" ? "noavailable" : "available"}/>
                </div>
                {renderIndicator()}
            </div>
            {renderScanDetail()}
        </div>
    </Card>;
};
